/**
 * Профили площадок: подсказки прицелу, приезжающие с сервера ДАННЫМИ.
 *
 * Магазин расширений запрещает исполнять приехавший код — и правильно делает.
 * Настройки возить можно, и запрет от этого не обходится, а работает как задуман:
 * разбирает профиль код, вшитый в расширение, а с сервера едет только «где на
 * этой площадке обычно живут кэфы». Побочная выгода важнее самого запрета:
 * правка селектора перестаёт быть релизом и повторной модерацией, а по истории
 * конкурентов селекторы правятся почти еженедельно.
 *
 * Скачивает **страница**, а не расширение. Расширение не ходит в сеть вовсе,
 * кроме биржи, — это его свойство, за которое человек и ставит подтверждатель, и
 * менять его ради одного JSON нельзя. Лента и так открыта, у неё уже есть
 * сессия, и профили она передаёт тем же мостиком, что состав строк.
 *
 * Откат тут не «вшитая копия», и это осознанно. Прицел работает без подсказок
 * вообще: ячейку выбирает совпадение самого числа кэфа, подсказка лишь помогает
 * выбрать между одинаковыми (`aim.ts`). Поэтому запасных слоёв ровно два —
 * последний удачный ответ в `localStorage` и отсутствие подсказок. Третья копия
 * тех же селекторов внутри ленты была бы вторым источником правды, который
 * однажды разойдётся с первым, — за нулевую прибавку к возможностям.
 */

export const PROFILES = 'forq:profiles';

/** Что мы знаем о странице площадки. Всё остальное из ответа нам не нужно. */
export interface VenueProfile {
  /** Селекторы-подсказки: `odds` — где кэфы, `stake` — поле суммы. */
  aim: Record<string, string>;
  domains: string[];
}

export interface Profiles {
  version: string;
  /** Серверный выключатель клика. false — fill остаётся, click гасится у всех. */
  autoClick: boolean;
  venues: Record<string, VenueProfile>;
}

export const EMPTY: Profiles = { version: '', autoClick: false, venues: {} };

/** Сколько живёт запомненный ответ. Сутки: чаще селекторы не правят. */
export const KEEP_MS = 24 * 60 * 60 * 1_000;

const CACHE = 'forq:profiles:v1';

/**
 * Разобрать ответ сервера. `null` — это не профили.
 *
 * Селектор длиннее двухсот знаков — не селектор, а чей-то текст: такой в
 * `querySelector` уедет впустую, зато раздует сообщение в окно.
 */
export function parseProfiles(data: unknown): Profiles | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (typeof body.version !== 'string' || body.version === '') return null;
  if (typeof body.venues !== 'object' || body.venues === null) return null;
  const venues: Record<string, VenueProfile> = {};
  for (const [code, raw] of Object.entries(body.venues as Record<string, unknown>)) {
    if (typeof raw !== 'object' || raw === null) continue;
    const one = raw as Record<string, unknown>;
    const aim: Record<string, string> = {};
    for (const [part, selector] of Object.entries((one.aim ?? {}) as Record<string, unknown>)) {
      if (typeof selector === 'string' && selector !== '' && selector.length <= 200) {
        aim[part] = selector;
      }
    }
    const domains = Array.isArray(one.domains)
      ? one.domains.filter((host): host is string => typeof host === 'string' && host !== '')
      : [];
    venues[code] = { aim, domains };
  }
  return { version: body.version, autoClick: body.autoClick === true, venues };
}

/** Профиль по хосту вкладки. Поддомены считаются той же площадкой. */
export function profileFor(
  profiles: Profiles,
  host: string,
): { code: string; profile: VenueProfile } | null {
  const needle = host.trim().toLowerCase().replace(/^www\./, '');
  for (const [code, one] of Object.entries(profiles.venues)) {
    for (const domain of one.domains) {
      const plain = domain.toLowerCase().replace(/^www\./, '');
      if (needle === plain || needle.endsWith(`.${plain}`)) return { code, profile: one };
    }
  }
  return null;
}

/** Подсказка для прицела. Пусто — ищем по одному числу, и это рабочий случай. */
export const hintFor = (profiles: Profiles, host: string): string | undefined =>
  profileFor(profiles, host)?.profile.aim.odds;

interface Kept {
  at: number;
  body: Profiles;
}

/** Запомненный ответ. `null` — не запоминали или он протух. */
export function remembered(store: Storage = localStorage): Profiles | null {
  try {
    const raw = store.getItem(CACHE);
    if (raw === null) return null;
    const kept = JSON.parse(raw) as Kept;
    if (typeof kept?.at !== 'number' || Date.now() - kept.at > KEEP_MS) return null;
    return parseProfiles(kept.body);
  } catch {
    return null;
  }
}

export function remember(body: Profiles, store: Storage = localStorage): void {
  try {
    store.setItem(CACHE, JSON.stringify({ at: Date.now(), body } satisfies Kept));
  } catch {
    // Переполненное хранилище — не повод падать: без кэша просто спросим снова.
  }
}

/**
 * Забрать профили с сервера. Сеть молчит — отдаём запомненное, потом пустое.
 *
 * Пустое здесь — рабочее состояние, а не поломка: прицел ищет по числу и без
 * подсказок. Поэтому ошибку сети мы не показываем человеку: сказать «профили не
 * скачались» значит напугать его тем, чего он не заметит.
 */
export async function fetchProfiles(
  get: (url: string) => Promise<Response>,
  url: string,
  store: Storage | null = null,
): Promise<Profiles> {
  const box = store ?? (typeof localStorage === 'undefined' ? null : localStorage);
  try {
    const answer = await get(url);
    if (!answer.ok) throw new Error(String(answer.status));
    const body = parseProfiles(await answer.json());
    if (body === null) throw new Error('не профили');
    if (box !== null) remember(body, box);
    return body;
  } catch {
    return (box === null ? null : remembered(box)) ?? EMPTY;
  }
}

export const profilesEnvelope = (
  profiles: Profiles,
): { channel: string; profiles: Profiles } => ({ channel: PROFILES, profiles });

export function parseEnvelope(data: unknown): Profiles | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== PROFILES) return null;
  return parseProfiles(body.profiles);
}
